import React from "react"
import Grid from "@mui/material/Grid"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import MuiTextField from "components/MuiTextField"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiAsyncAutoComplete from "components/MuiAsyncAutocomplete"
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import FileUploaderV2 from "components/FileUploaderV2"
import MuiModal from "components/MuiModal"
import { DropdownObj } from "pages/Common/commonTypes"
import { buildComplaintPickerQueryArg } from "pages/Complaints/utils/complaintLogisticsPicker"
import { ComplaintSummaryCard, LockedValueField } from "./ComplaintReferenceFields"
import useLogisticBasicDetails, { ELogisticBasicDetailFields } from "./useLogisticBasicDetails"
import { EPurchaseOrderFields } from "../Projects/useProjectBasicDetails"
import { EChargersAndMaterialsFields } from "../../ChargersAndMaterials/useChargersAndMaterials"
import { tableCellStyles, tableHeaderCellStyles } from "../../ChargersAndMaterials"
import ProjectFormFooter from "components/ProjectForm/components/ProjectFormFooter"
import { ProjectFormPageProps } from "components/ProjectForm"
import PurchaseOrderItemsTable from "../PurchaseOrderItemsTable"

const LogisticBasicDetails = (props: ProjectFormPageProps) => {
    const { states, functions } = useLogisticBasicDetails(props)
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={4}>
                    {/* Two independent columns, as in the design: the left stacks the ticket's own
                        details, the right its categorisation. Keeping them as separate columns
                        rather than cells flowing across one grid is also what makes the left column
                        immune to the complaint block — mounting the picker or revealing the summary
                        card grows only the right column, so nothing on the left shifts. */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={3}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Project Name*</Typography>
                                <MuiTextField
                                    name={ELogisticBasicDetailFields.PROJECT_NAME}
                                    value={states.formik.values[ELogisticBasicDetailFields.PROJECT_NAME]}
                                    placeholder="Enter Project Name"
                                    size="large"
                                    onChange={states.formik.handleChange}
                                    onBlur={states.formik.handleBlur}
                                    error={functions.hasError(ELogisticBasicDetailFields.PROJECT_NAME)}
                                />
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="label">Purchase Order</Typography>
                                    {!states.showPurchaseOrderForm && (
                                        <Typography
                                            variant="label"
                                            onClick={functions.openPurchaseOrderForm}
                                            sx={{
                                                textDecoration: "underline",
                                                textUnderlineOffset: "2px",
                                                fontWeight: "400",
                                                color: "#0430C0",
                                                cursor: "pointer",
                                            }}>
                                            + Upload New
                                        </Typography>
                                    )}
                                </Box>
                                <MuiAutoComplete
                                    disablePortal={false}
                                    size="large"
                                    options={states.purchaseOrderOptions}
                                    placeholder="Select Existing PO"
                                    value={states.formik.values[ELogisticBasicDetailFields.PURCHASE_ORDER] || null}
                                    onChange={value =>
                                        states.formik.setFieldValue(ELogisticBasicDetailFields.PURCHASE_ORDER, value)
                                    }
                                />
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Requested Date of Delivery*</Typography>
                                <MuiTextField
                                    name={ELogisticBasicDetailFields.VISIT_BY_DATE}
                                    value={states.formik.values[ELogisticBasicDetailFields.VISIT_BY_DATE]}
                                    onChange={states.formik.handleChange}
                                    onBlur={states.formik.handleBlur}
                                    placeholder="Select date"
                                    type="date"
                                    size="large"
                                    error={functions.hasError(ELogisticBasicDetailFields.VISIT_BY_DATE)}
                                />
                                <MuiAutoComplete
                                    disablePortal={false}
                                    fullWidth
                                    disableClearable
                                    size="large"
                                    placeholder="Select Time of The Day"
                                    options={states.timeOfDayOptions}
                                    value={states.formik.values[ELogisticBasicDetailFields.VISIT_BY_TIME] || null}
                                    error={functions.hasError(ELogisticBasicDetailFields.VISIT_BY_TIME)}
                                    onChange={value =>
                                        states.formik.setFieldValue(ELogisticBasicDetailFields.VISIT_BY_TIME, value)
                                    }
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={3}>
                            {states.isComplaintLocked ? (
                                <LockedValueField
                                    label="Project Category"
                                    // Resolved from the category master, so the locked row shows the
                                    // master's own label rather than a hardcoded string.
                                    value={states.formik.values[ELogisticBasicDetailFields.CATEGORY]?.title || "—"}
                                    // hint={`Set from complaint ${states.selectedComplaint?.title ?? ""}. Cannot be changed here.`}
                                />
                            ) : (
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Project Category*</Typography>
                                    <MuiAutoComplete
                                        size="large"
                                        options={states.categoryOptions}
                                        placeholder="Select Project Category"
                                        loading={states.isCategoriesFetching}
                                        value={states.formik.values[ELogisticBasicDetailFields.CATEGORY] || null}
                                        onChange={value => functions.handleCategoryChange(value as DropdownObj | null)}
                                        error={functions.hasError(ELogisticBasicDetailFields.CATEGORY)}
                                    />
                                </Box>
                            )}

                            {/* Complaint Reference ID and its summary sit directly below Project
                                Category in this column, mounting only for the complaint-linked
                                categories — every other category's step looks exactly as before. */}
                            {states.isComplaintLinkedCategorySelected && (
                                <>
                                    {states.isComplaintLocked ? (
                                        <LockedValueField
                                            label="Complaint Reference ID / CIN Number"
                                            value={states.selectedComplaint?.title ?? ""}
                                        />
                                    ) : (
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Typography variant="label">
                                                Complaint Reference ID / CIN Number*
                                            </Typography>
                                            <MuiAsyncAutoComplete
                                                size="large"
                                                placeholder="Search complaint ID"
                                                trigger={states.complaintsTrigger}
                                                pageSize={states.complaintPickerPageSize}
                                                buildQueryArg={buildComplaintPickerQueryArg}
                                                selectData={functions.complaintOptionsSelector}
                                                value={states.selectedComplaint}
                                                onChange={functions.handleComplaintChange}
                                                error={functions.hasError(ELogisticBasicDetailFields.COMPLAINT)}
                                            />
                                            <Typography variant="label" color="#5B6982">
                                                Search By Complaint Id or CIN Number
                                            </Typography>
                                            {functions.hasError(ELogisticBasicDetailFields.COMPLAINT) && (
                                                <Typography fontSize="12px" color="error">
                                                    Pick the complaint this dispatch is for.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {states.selectedComplaintSummary && (
                                        <ComplaintSummaryCard
                                            complaint={states.selectedComplaintSummary}
                                            existingDispatchCount={states.existingLinkedCount}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    </Grid>
                    {states.showPurchaseOrderForm && (
                        <Grid container spacing={2} bgcolor="#FAFAFA" padding={"12px"} borderRadius={"8px"}>
                            <Grid size={12} fontSize={"16px"} fontWeight={"500"}>
                                NEW PO
                                <Typography
                                    variant="label"
                                    onClick={functions.closePurchaseOrderForm}
                                    fontSize={"12px"}
                                    sx={{
                                        textDecoration: "underline",
                                        textUnderlineOffset: "2px",
                                        fontWeight: "400",
                                        color: "statiqOrange.main",
                                        cursor: "pointer",
                                        marginLeft: "8px",
                                    }}>
                                    REMOVE
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">PO Name*</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.PO_NAME}
                                        placeholder="Enter PO Name"
                                        size="large"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_NAME]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_NAME)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">PO Client Number*</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.PO_NUMBER}
                                        placeholder="Enter PO Client Number"
                                        size="large"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_NUMBER]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_NUMBER)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Select Account*</Typography>
                                    <MuiAsyncAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select Account"
                                        trigger={states.accountsTrigger}
                                        buildQueryArg={({ page, page_size, search }) => ({
                                            page,
                                            page_size,
                                            search_key: search,
                                        })}
                                        selectData={res => ({
                                            options: res.data.map((a: any) => ({ id: a.id, title: a.account_name })),
                                            hasMore: res.have_more,
                                        })}
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.ACCOUNT]}
                                        onChange={value =>
                                            functions.handleAccountChange(value as { id: number; title: string } | null)
                                        }
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.ACCOUNT)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Select Billing Address*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select Billing Address"
                                        options={states.billingAddressOptions}
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.BILLING_ADDRESS]}
                                        onChange={value =>
                                            states.purchaseOrderFormik.setFieldValue(
                                                EPurchaseOrderFields.BILLING_ADDRESS,
                                                value
                                            )
                                        }
                                        loading={states.isFetchingBillingAddresses}
                                        disabled={
                                            !states.purchaseOrderFormik.values[EPurchaseOrderFields.ACCOUNT] ||
                                            states.isFetchingBillingAddresses
                                        }
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.BILLING_ADDRESS)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">PO Date*</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.PO_DATE}
                                        placeholder="Select date"
                                        type="date"
                                        size="large"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DATE]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_DATE)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">PO Receive Date*</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.PO_RECEIVE_DATE}
                                        placeholder="Select date"
                                        type="date"
                                        size="large"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_RECEIVE_DATE]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_RECEIVE_DATE)}
                                        slotProps={{
                                            htmlInput: {
                                                min:
                                                    states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DATE] ||
                                                    undefined,
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Deal Done by*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select Name"
                                        options={states.dealDoneByUsersOptions}
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DEAL_DONE_BY]}
                                        onChange={value =>
                                            states.purchaseOrderFormik.setFieldValue(
                                                EPurchaseOrderFields.PO_DEAL_DONE_BY,
                                                value
                                            )
                                        }
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_DEAL_DONE_BY)}
                                    />
                                </Box>
                            </Grid>

                            {/* PO Items Table */}
                            <Grid size={12}>
                                <PurchaseOrderItemsTable
                                    items={states.purchaseOrderFormik.values.items}
                                    onAddItem={functions.addNewItem}
                                    onRemoveItem={functions.removeItem}
                                    onUpdateItem={functions.updateItem}
                                    skuOptions={states.skuOptions}
                                    hasTableError={functions.hasItemsTableError}
                                    getItemFieldError={functions.getItemFieldError}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Shipping Charges*</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.SHIPPING_CHARGES}
                                        placeholder="Enter Shipping Charges"
                                        size="large"
                                        type="number"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                        error={functions.hasPurchaseOrderError(EPurchaseOrderFields.SHIPPING_CHARGES)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label" textTransform={"none"}>
                                        Total PO Value (w/o GST)*
                                    </Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.TOTAL_PO_VALUE}
                                        placeholder="Enter Total PO Value"
                                        size="large"
                                        type="number"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.TOTAL_PO_VALUE]}
                                        disabled
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Remarks</Typography>
                                    <MuiTextField
                                        name={EPurchaseOrderFields.REMARKS}
                                        placeholder="Enter Remarks"
                                        size="large"
                                        value={states.purchaseOrderFormik.values[EPurchaseOrderFields.REMARKS]}
                                        onChange={states.purchaseOrderFormik.handleChange}
                                        onBlur={states.purchaseOrderFormik.handleBlur}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Purchase Order</Typography>
                                    <FileUploaderV2 />
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    <Grid container size={12} alignItems="flex-start">
                        <Box display="flex" flexDirection="column" gap={2} width="100%">
                            <Box display="flex" flexDirection="column" gap={2} width="100%">
                                <Typography fontSize={14} variant="label" color="statiqOrange.main">
                                    POC DETAILS
                                </Typography>

                                <Typography fontSize={12} variant="label" color="textPrimary">
                                    Point of Contact (Source)
                                </Typography>
                                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                    <Box flex={1} display="flex" flexDirection="column" gap={1}>
                                        <MuiTextField
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.SOURCE_POC]
                                                    .on_ground_poc_name
                                            }
                                            name={`${ELogisticBasicDetailFields.SOURCE_POC}.on_ground_poc_name`}
                                            fullWidth
                                            onChange={states.formik.handleChange}
                                            size="large"
                                            placeholder="Enter POC Name"
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.SOURCE_POC,
                                                "on_ground_poc_name"
                                            )}
                                        />
                                    </Box>

                                    <Box flex={1} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                        <MuiTextField
                                            fullWidth
                                            size="large"
                                            placeholder="Enter POC Contact"
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.SOURCE_POC]
                                                    .on_ground_poc_phone
                                            }
                                            name={`${ELogisticBasicDetailFields.SOURCE_POC}.on_ground_poc_phone`}
                                            onChange={e => {
                                                const value = e.target.value.replace(/\D/g, "")
                                                states.formik.setFieldValue(
                                                    `${ELogisticBasicDetailFields.SOURCE_POC}.on_ground_poc_phone`,
                                                    value
                                                )
                                            }}
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: 10,
                                                },
                                            }}
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.SOURCE_POC,
                                                "on_ground_poc_phone"
                                            )}
                                        />
                                        <MuiTextField
                                            fullWidth
                                            size="large"
                                            placeholder="Enter POC Email"
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.SOURCE_POC]
                                                    .on_ground_poc_email
                                            }
                                            name={`${ELogisticBasicDetailFields.SOURCE_POC}.on_ground_poc_email`}
                                            onChange={states.formik.handleChange}
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.SOURCE_POC,
                                                "on_ground_poc_email"
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1} width="100%">
                                <Typography fontSize={12} variant="label" color="textPrimary">
                                    Point of Contact (Destination)*
                                </Typography>
                                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                    <Box flex={1} display="flex" flexDirection="column" gap={1}>
                                        <MuiTextField
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.DESTINATION_POC]
                                                    .on_ground_poc_name
                                            }
                                            name={`${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_name`}
                                            fullWidth
                                            onChange={states.formik.handleChange}
                                            size="large"
                                            placeholder="Enter POC Name"
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.DESTINATION_POC,
                                                "on_ground_poc_name"
                                            )}
                                        />
                                    </Box>

                                    <Box flex={1} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                        <MuiTextField
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.DESTINATION_POC]
                                                    .on_ground_poc_phone
                                            }
                                            name={`${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_phone`}
                                            onChange={e => {
                                                const value = e.target.value.replace(/\D/g, "")
                                                states.formik.setFieldValue(
                                                    `${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_phone`,
                                                    value
                                                )
                                            }}
                                            fullWidth
                                            size="large"
                                            placeholder="Enter POC Contact"
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: 10,
                                                },
                                            }}
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.DESTINATION_POC,
                                                "on_ground_poc_phone"
                                            )}
                                        />
                                        <MuiTextField
                                            fullWidth
                                            size="large"
                                            name={`${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_email`}
                                            onChange={states.formik.handleChange}
                                            value={
                                                states.formik.values[ELogisticBasicDetailFields.DESTINATION_POC]
                                                    .on_ground_poc_email
                                            }
                                            placeholder="Enter POC Email"
                                            error={functions.hasPocError(
                                                ELogisticBasicDetailFields.DESTINATION_POC,
                                                "on_ground_poc_email"
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={false}
                handleSubmit={() => states.formik.handleSubmit()}
            />

            {/* Leaving a complaint-linked category drops the complaint reference, so it is confirmed
                rather than silently discarded. */}
            <MuiModal
                width="420px"
                open={states.isCategoryChangeConfirmOpen}
                onClose={functions.cancelCategoryChange}
                showCloseIcon
                header="Change project category?"
                showFooter
                cancelButtonText="Cancel"
                submitButtonText="Continue"
                onSave={functions.confirmCategoryChange}>
                <Box padding="var(--mui-modal-padding)">
                    <Typography fontSize="14px">
                        Changing the category will remove complaint <strong>{states.selectedComplaint?.title}</strong>{" "}
                        from this ticket. Continue?
                    </Typography>
                </Box>
            </MuiModal>
        </>
    )
}

export default LogisticBasicDetails
